import {
  OpenClass,
  OpenClassCertificateOffering,
  OpenClassRegistration,
} from '../types/OpenClass';
import {
  earnsFurboAttendanceAttest,
  lacksCertificateEmailRecipient,
  passedOpenClass,
  usesUploadedCertificate,
} from './certificateEligibility';
import { ClassAttendeeSession, classAttendees } from './openClassAttendees';

// Where a certificate actually lands. The attendee's own address when they have one, the
// order's contact person when they don't.
export type SendDestination = 'contact' | 'none' | 'own';

export type SendCandidate = {
  // A failed test behind full attendance still gets the Furbo attendance attest
  attendanceOnly: boolean;
  certificateFileName?: string | null;
  destination: SendDestination;
  email?: string | null;
  name: string;
  registrationId: OpenClassRegistration['id'];
  sentAt?: string | null;
  sentTo?: string | null;
  sessions: ClassAttendeeSession[];
};

export type SendGroup = {
  candidates: SendCandidate[];
  template: OpenClassCertificateOffering;
};

// Read off the state the registration already carries. The rules for earning a certificate
// live in certificateEligibility, this only says which one applied.
export type ExclusionReason =
  | 'failed'
  | 'missingCertificate'
  | 'notRecorded'
  | 'unreachable';

export type ExcludedCandidate = {
  email?: string | null;
  name: string;
  reason: ExclusionReason;
  registrationId: OpenClassRegistration['id'];
  sessions: ClassAttendeeSession[];
};

export type CertificateSendPlan = {
  excluded: ExcludedCandidate[];
  groups: SendGroup[];
};

const destinationFor = (
  registration: OpenClassRegistration,
): Pick<SendCandidate, 'destination' | 'email'> => {
  const ownEmail = registration.attendee.email?.trim();
  if (ownEmail) return { destination: 'own', email: ownEmail };
  if (registration.orderHasContact === false) return { destination: 'none' };
  return { destination: 'contact' };
};

const exclusionReason = (
  registration: OpenClassRegistration,
): ExclusionReason | undefined => {
  if (registration.certificateResultStatus === 'failed') {
    if (!earnsFurboAttendanceAttest(registration)) return 'failed';
  } else if (!passedOpenClass(registration)) {
    return 'notRecorded';
  }
  if (
    usesUploadedCertificate(registration.certificateType) &&
    !registration.besaccCertificateFileName
  ) {
    return 'missingCertificate';
  }
  if (lacksCertificateEmailRecipient(registration)) {
    return 'unreachable';
  }
  return undefined;
};

const byName = (left: { name: string }, right: { name: string }) =>
  left.name.localeCompare(right.name);

// Everyone in the class lands somewhere, either under the certificate they are getting or in
// the excluded list with the reason attached. Nobody is dropped without a trace.
export const buildCertificateSendPlan = (
  openClass: OpenClass,
  registrations: OpenClassRegistration[],
): CertificateSendPlan => {
  const candidatesByTemplate = new Map<number, SendCandidate[]>();
  const excluded: ExcludedCandidate[] = [];
  // The class wide list already resolves each attendance to the session it belongs to.
  const sessionsByRegistration = new Map(
    classAttendees(registrations, openClass.sessions).map((attendee) => [
      attendee.registrationId,
      attendee.sessions,
    ]),
  );

  registrations.forEach((registration) => {
    const reason = exclusionReason(registration);
    if (reason) {
      excluded.push({
        email: registration.attendee.email,
        name: registration.attendee.name,
        reason,
        registrationId: registration.id,
        sessions: sessionsByRegistration.get(registration.id) ?? [],
      });
      return;
    }

    const candidates =
      candidatesByTemplate.get(registration.certificateTemplateId) ?? [];
    candidates.push({
      ...destinationFor(registration),
      attendanceOnly: earnsFurboAttendanceAttest(registration),
      certificateFileName: registration.besaccCertificateFileName,
      name: registration.attendee.name,
      registrationId: registration.id,
      sentAt: registration.certificateSentAt,
      sentTo: registration.certificateSentTo,
      sessions: sessionsByRegistration.get(registration.id) ?? [],
    });
    candidatesByTemplate.set(registration.certificateTemplateId, candidates);
  });

  const groups = openClass.certificateTemplates.flatMap((template) => {
    const candidates = candidatesByTemplate.get(template.id);
    if (!candidates?.length) return [];
    return [{ candidates: candidates.sort(byName), template }];
  });

  return { excluded: excluded.sort(byName), groups };
};

export const canReceive = (candidate: SendCandidate) =>
  candidate.destination !== 'none';

// Ticked by default, so the common case is one click. Anything already delivered starts
// clear, which keeps a resend deliberate.
export const defaultSelection = (plan: CertificateSendPlan): Set<number> =>
  new Set(
    plan.groups.flatMap(({ candidates }) =>
      candidates
        .filter((candidate) => !candidate.sentAt && canReceive(candidate))
        .map(({ registrationId }) => registrationId),
    ),
  );

export const selectionCounts = (
  plan: CertificateSendPlan,
  selected: Set<number>,
): { alreadySent: number; mailed: number } => {
  const candidates = plan.groups.flatMap(({ candidates }) => candidates);
  return {
    alreadySent: candidates.filter(
      ({ registrationId, sentAt }) => sentAt && !selected.has(registrationId),
    ).length,
    mailed: candidates.filter(({ registrationId }) =>
      selected.has(registrationId),
    ).length,
  };
};
